import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Type, Image, QrCode, Square, Layers, Save, Eye, Trash2, Copy, Move, ScanLine, Lock, Unlock, EyeOff, AlignCenter, AlignHorizontalJustifyCenter } from "lucide-react";
import QRCode from "qrcode";
import {
  BADGE_PRESETS,
  BADGE_STARTER_TEMPLATES,
  encodeBadgeQr,
  fetchBadgeDataSources,
  fetchBadgeTemplate,
  materializeBadgeLayers,
  normalizeBadgeTemplate,
  saveBadgeTemplate,
} from "@/lib/badge-template";
import BadgePrintModal from "@/components/BadgePrintModal";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import WorkspaceMediaField from "@/components/WorkspaceMediaField";
import { resolveMediaUrl } from "@/lib/media-api";

export default function BadgeDesigner() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const [template, setTemplate] = useState({
    name: "Default Badge",
    preset: "badge_a6_portrait",
    size: { unit: "mm", width: 105, height: 148 },
    layers: [],
    variants: { attendee: true, staff: true, vip: true },
    logoUrl: "",
  });
  const [active, setActive] = useState(null);
  const [statusLabel, setStatusLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [dataSources, setDataSources] = useState(null);
  const [sampleType, setSampleType] = useState("attendee");
  const [snap, setSnap] = useState(true);
  const [gridStep, setGridStep] = useState(8);
  const [startMode, setStartMode] = useState("template");
  const [starterId, setStarterId] = useState("conference_standard");
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const saveTimer = useRef(null);

  const layers = template.layers || [];
  const sel = layers.find((l) => l.id === active) || null;

  const selectedPreset = useMemo(
    () => BADGE_PRESETS.find((p) => p.id === template.preset) || BADGE_PRESETS[0] || { label: "Custom", widthMm: 105, heightMm: 148 },
    [template.preset],
  );
  const selectedStarter = useMemo(
    () => BADGE_STARTER_TEMPLATES.find((item) => item.id === starterId) || BADGE_STARTER_TEMPLATES[0],
    [starterId],
  );
  const canvasPx = useMemo(() => ({
    w: Math.max(220, Math.round((template.size?.width || selectedPreset.widthMm) * 4.2)),
    h: Math.max(120, Math.round((template.size?.height || selectedPreset.heightMm) * 4.2)),
  }), [template.size?.width, template.size?.height, selectedPreset.widthMm, selectedPreset.heightMm]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const [tpl, sources] = await Promise.all([fetchBadgeTemplate(id), fetchBadgeDataSources(id)]);
        if (!active) return;
        const next = normalizeBadgeTemplate(tpl);
        setTemplate(next);
        setActive(next.layers?.[0]?.id || null);
        setDataSources(sources);
      } catch {
        if (active) toast.error("Could not load badge template");
      } finally {
        // Always clear the loading overlay — guarding this with `active` left the
        // canvas stuck on "Loading template..." under React Strict Mode remounts.
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (loading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setStatusLabel("Saving...");
        await saveBadgeTemplate(id, template);
        setStatusLabel("Saved");
        setTimeout(() => setStatusLabel(""), 1800);
      } catch {
        setStatusLabel("Save failed");
      }
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [template, id, loading]);

  useEffect(() => {
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { mode, layerId, offsetX, offsetY, startW, startH, startX, startY } = dragRef.current;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      setTemplate((prev) => ({
        ...prev,
        layers: (prev.layers || []).map((l) => {
          if (l.id !== layerId) return l;
          if (mode === "move") {
            const maxX = canvasPx.w - (l.w || 1);
            const maxY = canvasPx.h - (l.h || 1);
            const nx = clamp(x - offsetX, 0, maxX);
            const ny = clamp(y - offsetY, 0, maxY);
            return {
              ...l,
              x: snap ? clamp(snapTo(nx, gridStep), 0, maxX) : nx,
              y: snap ? clamp(snapTo(ny, gridStep), 0, maxY) : ny,
            };
          }
          if (mode === "resize") {
            const nw = clamp(startW + (x - startX), 28, canvasPx.w);
            const nh = clamp(startH + (y - startY), 20, canvasPx.h);
            return {
              ...l,
              w: snap ? clamp(snapTo(nw, gridStep), 28, canvasPx.w) : nw,
              h: snap ? clamp(snapTo(nh, gridStep), 20, canvasPx.h) : nh,
            };
          }
          return l;
        }),
      }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [canvasPx.w, canvasPx.h, snap, gridStep]);

  const updateTemplate = (patch) => setTemplate((prev) => ({ ...prev, ...patch }));
  const updateLayer = (layerId, patch) =>
    setTemplate((prev) => ({ ...prev, layers: (prev.layers || []).map((l) => (l.id === layerId ? { ...l, ...patch } : l)) }));

  const addLayer = (type) => {
    const idValue = `l_${Date.now()}`;
    const base = {
      id: idValue,
      type,
      x: 16,
      y: 16,
      w: type === "qr" ? 120 : type === "shape" ? 180 : type === "image" ? 220 : 200,
      h: type === "qr" ? 120 : type === "shape" ? 42 : type === "image" ? 120 : 34,
      content:
        type === "text"
          ? "New text"
          : type === "shape"
            ? "{{attendee.ticket}}"
            : type === "qr"
              ? "{{qr.code}}"
              : type === "image"
                ? e.cover
                : "",
      fontSize: 18,
      fontWeight: 600,
      fill: "#09090B",
      textColor: e.accent || "var(--primary)",
      locked: false,
      hidden: false,
    };
    updateTemplate({ layers: [...layers, base] });
    setActive(idValue);
  };

  const insertBrandLogo = () => {
    const logo = (template.logoUrl || "").trim();
    if (!logo) {
      toast.error("Add your logo URL first");
      return;
    }
    const newId = `l_logo_${Date.now()}`;
    const logoLayer = {
      id: newId,
      type: "image",
      x: 16,
      y: 16,
      w: 120,
      h: 42,
      content: logo,
      locked: false,
      hidden: false,
    };
    updateTemplate({ layers: [...layers, logoLayer] });
    setActive(newId);
    toast.success("Logo inserted");
  };

  const duplicateLayer = () => {
    if (!sel) return;
    const copy = { ...sel, id: `l_${Date.now()}`, x: clamp((sel.x || 0) + 12, 0, canvasPx.w), y: clamp((sel.y || 0) + 12, 0, canvasPx.h) };
    updateTemplate({ layers: [...layers, copy] });
    setActive(copy.id);
  };

  const removeLayer = () => {
    if (!sel) return;
    const next = layers.filter((x) => x.id !== sel.id);
    updateTemplate({ layers: next });
    setActive(next[0]?.id || null);
  };

  const bindToken = (token) => {
    if (!sel) return;
    updateLayer(sel.id, { content: token });
  };

  const alignActive = (axis) => {
    if (!sel) return;
    if (axis === "center-x") updateLayer(sel.id, { x: Math.round((canvasPx.w - (sel.w || 0)) / 2) });
    if (axis === "center-y") updateLayer(sel.id, { y: Math.round((canvasPx.h - (sel.h || 0)) / 2) });
  };

  const updatePreset = (presetId) => {
    const p = BADGE_PRESETS.find((x) => x.id === presetId);
    if (!p) return;
    updateTemplate({ preset: presetId, size: { unit: "mm", width: p.widthMm, height: p.heightMm } });
  };

  const applyStarterTemplate = (mode = "replace") => {
    if (!selectedStarter) return;
    const baseY =
      mode === "append"
        ? layers.reduce((max, layer) => Math.max(max, Number(layer.y || 0) + Number(layer.h || 0) + 12), 0)
        : 0;
    const nextLayers = materializeBadgeLayers(selectedStarter.layers).map((layer) => ({
      ...layer,
      y: Number(layer.y || 0) + baseY,
    }));
    const shouldUseRecommendedPreset = selectedStarter.recommendedPreset && selectedStarter.recommendedPreset !== "custom";
    const recommendedPreset = shouldUseRecommendedPreset ? BADGE_PRESETS.find((x) => x.id === selectedStarter.recommendedPreset) : null;
    const nextTemplatePatch = {};
    if (recommendedPreset) {
      nextTemplatePatch.preset = recommendedPreset.id;
      nextTemplatePatch.size = { unit: "mm", width: recommendedPreset.widthMm, height: recommendedPreset.heightMm };
    }
    nextTemplatePatch.layers = mode === "append" ? [...layers, ...nextLayers] : nextLayers;
    updateTemplate(nextTemplatePatch);
    setActive(nextLayers[0]?.id || null);
    toast.success(mode === "append" ? "Template appended" : "Template applied");
  };

  const applyBlankCanvas = () => {
    const custom = BADGE_PRESETS.find((item) => item.id === "custom");
    updateTemplate({
      preset: "custom",
      size: {
        unit: "mm",
        width: custom?.widthMm || 105,
        height: custom?.heightMm || 148,
      },
      layers: [],
    });
    setActive(null);
    setStartMode("custom");
    toast.success("Started from blank canvas");
  };

  const forceSave = async () => {
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      setStatusLabel("Saving...");
      await saveBadgeTemplate(id, template);
      setStatusLabel("Saved");
      setTimeout(() => setStatusLabel(""), 1800);
    } catch {
      setStatusLabel("Save failed");
      toast.error("Could not save template");
    }
  };

  const generateQrForSample = async () => {
    if (!sel || sel.type !== "qr") return;
    const sample = dataSources?.[sampleType]?.sample || {};
    const subjectId = sample?.id;
    if (!subjectId) {
      toast.error("No sample record found for QR generation");
      return;
    }
    try {
      const code = await encodeBadgeQr(id, sampleType === "attendee" ? "attendee" : sampleType === "staff" ? "staff" : "vip", subjectId);
      updateLayer(sel.id, { content: code });
      toast.success("QR payload generated for sample");
    } catch {
      toast.error("Could not generate QR payload");
    }
  };

  const qrSubjectType = sampleType === "staff" ? "staff" : sampleType === "vip" ? "vip" : "attendee";
  const qrSubjectId = dataSources?.[sampleType]?.sample?.id || dataSources?.attendee?.sample?.id;

  return (
    <div className="h-full min-h-0 flex flex-col" data-testid="badge-designer">
      {printModalOpen && dataSources ? (
        <BadgePrintModal
          open={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          eventId={id}
          template={template}
          dataSources={dataSources}
          subjectType={qrSubjectType}
          subjectId={qrSubjectId}
          eventTitle={e.title}
          footnote={`Sample: ${sampleType} · ${selectedPreset.label}`}
        />
      ) : null}
      <div className="border-b border-[var(--border-default)] h-16 px-6 flex items-center justify-between bg-[var(--bg-base)]">
        <div className="space-y-0.5">
          <div className="text-overline">{e.title} · badge designer</div>
          <div className="text-sm text-[#E4E4E7]">{selectedPreset.label} · {template.size?.width} × {template.size?.height} {template.size?.unit || "mm"}</div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-[var(--text-secondary)]">
            <span className="px-2 py-1 border border-[var(--border-default)]">Layers {layers.length}</span>
            <span className="px-2 py-1 border border-[var(--border-default)]">Sample {sampleType}</span>
          </div>
          {statusLabel ? <div className="text-xs font-mono text-[var(--text-secondary)] self-center mr-1 px-2 py-1 border border-[var(--border-default)]">{statusLabel}</div> : null}
          <button className="btn-secondary text-sm" onClick={() => setPrintModalOpen(true)} type="button" data-testid="badge-open-print-modal" disabled={loading || !dataSources} title={!dataSources ? "Loading data sources…" : ""}><Eye size={14} /> Preview &amp; print</button>
          <button className="btn-primary" onClick={forceSave} type="button"><Save size={14} /> Save template</button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-12 overflow-hidden">
        {/* Left: tools + layers */}
        <div className="col-span-3 min-w-0 min-h-0 border-r border-[var(--border-default)] flex flex-col overflow-hidden bg-[var(--bg-surface)]">
          <div className="p-4 border-b border-[var(--border-default)] max-h-[46vh] overflow-y-auto">
            <div className="text-overline mb-2">Template</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                className={`text-xs py-2 border transition ${startMode === "template" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--bg-surface)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                onClick={() => setStartMode("template")}
                type="button"
              >
                Use template
              </button>
              <button
                className={`text-xs py-2 border transition ${startMode === "custom" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--bg-surface)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                onClick={() => setStartMode("custom")}
                type="button"
              >
                Build custom
              </button>
            </div>
            {startMode === "template" ? (
              <div className="space-y-2 mb-3">
                <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto pr-1">
                  {BADGE_STARTER_TEMPLATES.filter((item) => item.id !== "blank_custom").map((item) => (
                    <button
                      key={item.id}
                      className={`text-left border p-2 transition ${starterId === item.id ? "border-[var(--primary)] bg-[var(--bg-elev)] ring-1 ring-[var(--primary)]/30" : "border-[var(--border-default)] hover:border-[var(--border-hover)]"}`}
                      onClick={() => setStarterId(item.id)}
                      type="button"
                    >
                      <div className="text-xs font-medium">{item.label}</div>
                      <div className="text-[10px] text-[var(--text-secondary)] mt-1">{item.description}</div>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button className="btn-secondary text-xs justify-center" onClick={() => applyStarterTemplate("replace")} type="button">
                    Apply
                  </button>
                  <button className="btn-secondary text-xs justify-center" onClick={() => applyStarterTemplate("append")} type="button">
                    Append
                  </button>
                </div>
                <TemplateMiniPreview item={selectedStarter} />
              </div>
            ) : (
              <div className="mb-3">
                <button className="btn-secondary w-full justify-center" onClick={applyBlankCanvas} type="button">
                  Start blank canvas
                </button>
              </div>
            )}
            <input className="input-dark mb-2" value={template.name} onChange={(ev) => updateTemplate({ name: ev.target.value })} />
            <select className="input-dark text-sm mb-2" value={template.preset} onChange={(ev) => updatePreset(ev.target.value)}>
              {BADGE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" className="input-dark font-mono" value={template.size?.width || 0} onChange={(ev) => updateTemplate({ preset: "custom", size: { ...template.size, width: Number(ev.target.value || 0) } })} />
              <input type="number" className="input-dark font-mono" value={template.size?.height || 0} onChange={(ev) => updateTemplate({ preset: "custom", size: { ...template.size, height: Number(ev.target.value || 0) } })} />
            </div>
            <div className="mt-2">
              <WorkspaceMediaField
                label="Brand logo"
                value={template.logoUrl || ""}
                onChange={(next) => updateTemplate({ logoUrl: next })}
              />
              <button className="btn-secondary text-xs w-full justify-center mt-2" onClick={insertBrandLogo} type="button">
                Insert logo layer
              </button>
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-xs text-[var(--text-secondary)]">
              <input type="checkbox" checked={snap} onChange={(ev) => setSnap(ev.target.checked)} className="accent-[var(--primary)]" />
              Snap to grid
            </label>
            <div className="mt-2">
              <label className="text-overline">Grid step</label>
              <select className="input-dark mt-2 text-xs" value={gridStep} onChange={(ev) => setGridStep(Number(ev.target.value || 8))}>
                <option value={4}>4 px</option>
                <option value={8}>8 px</option>
                <option value={12}>12 px</option>
                <option value={16}>16 px</option>
              </select>
            </div>
          </div>
          <div className="p-4 border-b border-[var(--border-default)] bg-[var(--bg-base)]">
            <div className="text-overline mb-3">Insert</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                [Type, "text", "Text"], [Image, "image", "Image"], [QrCode, "qr", "QR"], [Square, "shape", "Shape"],
              ].map(([Icon, type, label]) => (
                <button key={label} onClick={() => addLayer(type)} className="surface p-3 flex flex-col items-center gap-1 hover:border-[var(--primary)] hover:text-[var(--primary)] transition border bg-[var(--bg-elev)]" data-testid={`insert-${label.toLowerCase()}`}>
                  <Icon size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="flex items-center gap-2 text-overline mb-3"><Layers size={11} /> Layers</div>
            <div className="space-y-1">
              {[...layers].reverse().map((l) => (
                <button
                  key={l.id}
                  onClick={() => setActive(l.id)}
                  className={`w-full text-left text-xs px-3 py-2 transition border ${
                    active === l.id ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]" : "border-[var(--border-default)] hover:border-[var(--border-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--bg-elev)]"
                  }`}
                  data-testid={`layer-${l.id}`}
                >
                  <span className="font-mono uppercase tracking-widest text-[10px] mr-2">{l.type}</span>
                  <span className="truncate">{l.content || l.id}</span>
                  {l.locked ? <Lock size={11} className="inline ml-1" /> : null}
                  {l.hidden ? <EyeOff size={11} className="inline ml-1" /> : null}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Center: canvas */}
        <div className="col-span-6 min-w-0 min-h-0 bg-[var(--bg-elev)] dot-grid flex items-center justify-center overflow-auto p-10">
          <div
            ref={canvasRef}
            className="bg-[#FAFAFA] text-[#09090B] shadow-[0_30px_80px_rgba(0,0,0,0.6)] relative"
            style={{ width: canvasPx.w, height: canvasPx.h }}
          >
            {snap ? (
              <div
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                  backgroundImage: `linear-gradient(to right, rgba(9,9,11,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(9,9,11,0.08) 1px, transparent 1px)`,
                  backgroundSize: `${gridStep}px ${gridStep}px`,
                }}
              />
            ) : null}
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-3 z-0" style={{ background: e.accent || "var(--primary)" }} />
            {loading && layers.length === 0 ? (
              <div className="absolute inset-0 grid place-items-center text-sm text-[var(--text-tertiary)] z-10 bg-[#FAFAFA]/80 pointer-events-none">
                Loading template...
              </div>
            ) : null}
            {layers.map((l) => (
              <Layer
                key={l.id}
                l={l}
                active={active === l.id}
                onClick={() => setActive(l.id)}
                onDragStart={(ev) => {
                  ev.stopPropagation();
                  const rect = canvasRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  if (l.locked) return;
                  dragRef.current = { mode: "move", layerId: l.id, offsetX: ev.clientX - rect.left - (l.x || 0), offsetY: ev.clientY - rect.top - (l.y || 0) };
                }}
                onResizeStart={(ev) => {
                  ev.stopPropagation();
                  const rect = canvasRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  if (l.locked) return;
                  dragRef.current = { mode: "resize", layerId: l.id, startW: l.w || 80, startH: l.h || 24, startX: ev.clientX - rect.left, startY: ev.clientY - rect.top };
                }}
                previewValue={resolveToken(l.content, dataSources, sampleType)}
              />
            ))}
            {/* Bottom rail */}
            <div className="absolute bottom-0 left-0 right-0 px-6 py-3 flex items-center justify-between border-t border-[#09090B]/10 z-0 pointer-events-none">
              <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-[var(--text-tertiary)]">{e.title} · {e.startDate}</span>
              <span className="text-[9px] font-mono text-[var(--text-tertiary)]">#{String(dataSources?.[sampleType]?.sample?.id || e.id || "badge").slice(0, 16)}</span>
            </div>
          </div>
        </div>

        {/* Right: properties */}
        <div className="col-span-3 min-w-0 min-h-0 border-l border-[var(--border-default)] overflow-y-auto overflow-x-hidden p-5 bg-[var(--bg-surface)]">
          <div className="text-overline mb-4">Properties</div>
          {sel ? (
            <div className="space-y-4">
              <div className="surface p-3 bg-[var(--bg-surface)]">
                <div className="text-overline mb-2">Layer</div>
                <div className="text-sm font-mono uppercase">{sel.type}</div>
              </div>
              {(sel.content !== undefined) && (
                <div>
                  <label className="text-overline">Content</label>
                  <input className="input-dark mt-2" value={sel.content || ""} onChange={(ev) => updateLayer(sel.id, { content: ev.target.value })} data-testid="layer-content-input" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-overline">X</label><input className="input-dark mt-2 font-mono" value={sel.x} onChange={(ev) => updateLayer(sel.id, { x: Number(ev.target.value || 0) })} /></div>
                <div><label className="text-overline">Y</label><input className="input-dark mt-2 font-mono" value={sel.y} onChange={(ev) => updateLayer(sel.id, { y: Number(ev.target.value || 0) })} /></div>
                <div><label className="text-overline">W</label><input className="input-dark mt-2 font-mono" value={sel.w} onChange={(ev) => updateLayer(sel.id, { w: Number(ev.target.value || 0) })} /></div>
                <div><label className="text-overline">H</label><input className="input-dark mt-2 font-mono" value={sel.h} onChange={(ev) => updateLayer(sel.id, { h: Number(ev.target.value || 0) })} /></div>
              </div>
              {(sel.type === "text" || sel.type === "shape") && (
                <div>
                  <label className="text-overline">Font size</label>
                  <input type="range" min="8" max="90" value={sel.fontSize || 16} className="w-full accent-[var(--primary)] mt-2" onChange={(ev) => updateLayer(sel.id, { fontSize: Number(ev.target.value || 16) })} />
                  <div className="font-mono text-xs text-[var(--text-tertiary)] mt-1">{sel.fontSize || 16}pt</div>
                </div>
              )}
              {sel.type === "image" ? (
                <WorkspaceMediaField
                  label="Image"
                  value={sel.content || ""}
                  onChange={(v) => updateLayer(sel.id, { content: v })}
                />
              ) : null}
              {sel.type === "qr" ? (
                <button className="btn-secondary w-full justify-center" type="button" onClick={generateQrForSample}>
                  <ScanLine size={12} /> Generate sample QR payload
                </button>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={duplicateLayer}><Copy size={12} /> Duplicate</button>
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={() => updateLayer(sel.id, { fontWeight: (sel.fontWeight || 500) >= 700 ? 500 : 700 })}><Move size={12} /> Toggle weight</button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={() => alignActive("center-x")}><AlignCenter size={12} /> Center X</button>
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={() => alignActive("center-y")}><AlignHorizontalJustifyCenter size={12} /> Center Y</button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={() => updateLayer(sel.id, { locked: !sel.locked })}>
                  {sel.locked ? <Unlock size={12} /> : <Lock size={12} />} {sel.locked ? "Unlock" : "Lock"}
                </button>
                <button className="btn-secondary text-xs flex-1 min-w-[130px] justify-center" type="button" onClick={() => updateLayer(sel.id, { hidden: !sel.hidden })}>
                  {sel.hidden ? <Eye size={12} /> : <EyeOff size={12} />} {sel.hidden ? "Show" : "Hide"}
                </button>
              </div>
              <button className="btn-secondary w-full justify-center text-[#FF453A] border-[#FF453A]/40 hover:border-[#FF453A]" onClick={removeLayer} data-testid="layer-delete" type="button">
                <Trash2 size={12} /> Delete layer
              </button>
            </div>
          ) : <div className="text-sm text-[var(--text-tertiary)]">Select a layer.</div>}

          <div className="mt-10 surface p-4 bg-[var(--bg-surface)]">
            <div className="text-overline mb-2">Data source browser</div>
            <select className="input-dark mb-2" value={sampleType} onChange={(ev) => setSampleType(ev.target.value)}>
              <option value="attendee">Attendee</option>
              <option value="staff">Staff</option>
              <option value="vip">VIP / Speaker</option>
              <option value="event">Event</option>
            </select>
            <div className="space-y-1 max-h-52 overflow-y-auto text-xs font-mono">
              {((dataSources?.[sampleType]?.fields) || []).map((field) => (
                <button key={field} className="w-full min-w-0 text-left px-2 py-1 border border-transparent hover:border-[var(--border-default)] break-all" type="button" onClick={() => bindToken(`{{${sampleType}.${field}}}`)}>
                  {`{{${sampleType}.${field}}}`}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[var(--text-tertiary)] mt-2">Click a token to apply to selected layer content.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Layer({ l, active, onClick, onDragStart, onResizeStart, previewValue }) {
  if (l.hidden) return null;
  const label = String(previewValue ?? l.content ?? "").trim();
  const base = {
    position: "absolute",
    left: l.x,
    top: l.y,
    width: l.w,
    height: l.h,
    cursor: l.locked ? "not-allowed" : "pointer",
    outline: active ? "2px solid var(--primary)" : "1px dashed transparent",
    outlineOffset: active ? 2 : 0,
    userSelect: "none",
    overflow: "hidden",
    zIndex: 1,
  };
  if (l.type === "qr") {
    return (
      <div onClick={onClick} onMouseDown={onDragStart} style={base}>
        <RealQr value={previewValue || l.content || ""} />
        {active ? <ResizeHandle onMouseDown={onResizeStart} /> : null}
      </div>
    );
  }
  if (l.type === "image") {
    return (
      <div onClick={onClick} onMouseDown={onDragStart} style={{ ...base, overflow: "hidden" }} className="bg-white border border-[#09090B]/15">
        {l.content ? <img src={resolveMediaUrl(l.content)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-[var(--text-tertiary)]">Image URL</div>}
        {active ? <ResizeHandle onMouseDown={onResizeStart} /> : null}
      </div>
    );
  }
  if (l.type === "shape") {
    return (
      <div
        onClick={onClick}
        onMouseDown={onDragStart}
        style={{ ...base, background: l.fill || "#09090B", color: l.textColor || "var(--primary)", fontSize: l.fontSize || 14, fontWeight: l.fontWeight || 600 }}
        className="flex items-center justify-center font-mono text-xs uppercase tracking-widest px-2 text-center break-words"
      >
        {label}
        {active ? <ResizeHandle onMouseDown={onResizeStart} /> : null}
      </div>
    );
  }
  return (
    <div
      onClick={onClick}
      onMouseDown={onDragStart}
      style={{ ...base, fontSize: l.fontSize || 16, fontWeight: l.fontWeight || 500, lineHeight: 1.15 }}
      className="font-heading break-words whitespace-pre-wrap"
    >
      {label}
      {active ? <ResizeHandle onMouseDown={onResizeStart} /> : null}
    </div>
  );
}

function ResizeHandle({ onMouseDown }) {
  return <div onMouseDown={onMouseDown} className="absolute -right-1 -bottom-1 w-3 h-3 bg-[var(--primary)] border border-[#09090B] cursor-se-resize" />;
}

function RealQr({ value }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const next = await QRCode.toDataURL(value || "B1|pending-event|attendee|pending-subject", {
          errorCorrectionLevel: "M",
          margin: 0,
          color: { dark: "#09090B", light: "#FFFFFF" },
          width: 512,
        });
        if (alive) setSrc(next);
      } catch {
        if (alive) setSrc("");
      }
    })();
    return () => {
      alive = false;
    };
  }, [value]);
  if (!src) return <div className="w-full h-full bg-white" />;
  return <img src={src} alt="QR code" className="w-full h-full object-contain bg-white" />;
}

function resolveToken(content, dataSources, sampleType) {
  if (!content || typeof content !== "string") return content;
  return content.replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
    const token = String(raw || "").trim();
    const [group, key] = token.split(".");
    const source = dataSources?.[group];
    if (!source || !source.sample) return "";
    const value = source.sample?.[key];
    if (value === undefined || value === null || String(value).trim() === "") return "";
    return String(value);
  }).replace(/\s+·\s+/g, " · ").replace(/^\s*·\s*|\s*·\s*$/g, "").trim();
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function snapTo(n, step = 4) {
  return Math.round(n / step) * step;
}

function TemplateMiniPreview({ item }) {
  if (!item) return null;
  const qrRight = item.id === "conference_standard" || item.id === "vip_speaker";
  return (
    <div className="border border-[var(--border-default)] p-2">
      <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-widest mb-1">Preview</div>
      <div className="bg-[#FAFAFA] text-[#09090B] h-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-[var(--primary)]" />
        <div className="absolute left-2 top-3 text-[7px] uppercase tracking-widest text-[var(--text-tertiary)]">{item.preview?.kicker || "EVENT"}</div>
        <div className="absolute left-2 top-5 text-[8px] font-semibold">{item.preview?.title || "NAME"}</div>
        {item.preview?.sub ? <div className="absolute left-2 top-7 text-[7px] text-[#52525B]">{item.preview.sub}</div> : null}
        {item.preview?.strip ? <div className="absolute left-2 top-10 text-[7px] px-1 py-[1px] bg-[#09090B] text-[var(--primary)]">{item.preview.strip}</div> : null}
        {item.preview?.qr ? (
          <div className={`absolute bottom-2 w-6 h-6 border border-[#09090B] ${qrRight ? "right-2" : "left-2"}`} />
        ) : null}
      </div>
    </div>
  );
}
